import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useRef, useState } from 'react';
import {
  FlatList,
  FlatListProps,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  View,
} from 'react-native';
import { HorizontalScrollButton } from './HorizontalScrollButton';
import { ListMask } from './ListMask';

const kScrollButtonSize = 60;

type Props = {
  noMask?: boolean;
  maskLocation?: number;
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const DecoratedHorizontalFlatListImpl = <T extends unknown>({
  onScroll,
  onLayout,
  onContentSizeChange,
  style,
  noMask,
  maskLocation,
  ...props
}: FlatListProps<T> & Props) => {
  const { rpx } = useResponsiveContext();
  const [showLeftMask, setShowLeftMask] = useState(false);
  const [showRightMask, setShowRightMask] = useState(true);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const contentOffset = useRef<number>(0);

  const listRef = useRef<FlatList>(null);

  const onListLayout = usePersistFn((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
    onLayout?.(e);
  });

  const onListContentSizeChange = usePersistFn(
    (width: number, height: number) => {
      setContentWidth(width);
      onContentSizeChange?.(width, height);
    },
  );

  const onListScroll = usePersistFn(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offset = e.nativeEvent.contentOffset.x;
      contentOffset.current = offset;
      const isLeftMaskVisible = offset > 0;
      const isRightMaskVisible = offset + containerWidth < contentWidth;
      if (showLeftMask !== isLeftMaskVisible) {
        setShowLeftMask(isLeftMaskVisible);
      }
      if (showRightMask !== isRightMaskVisible) {
        setShowRightMask(isRightMaskVisible);
      }
      onScroll?.(e);
    },
  );

  const showScrollButtons =
    Platform.OS === 'web' && contentWidth > containerWidth;

  const onScrollButtonPress = usePersistFn((type: 'left' | 'right') => {
    const dir = type === 'left' ? 1 : -1;
    listRef.current?.scrollToOffset({
      offset: contentOffset.current + dir * contentWidth,
      animated: true,
    });
  });

  const buttonSize = rpx(kScrollButtonSize);

  return (
    <View
      style={style}
      css={`
        padding-left: ${showScrollButtons ? buttonSize / 4 + rpx(4) : 0}px;
        padding-right: ${showScrollButtons ? buttonSize / 4 + rpx(4) : 0}px;
      `}
    >
      <ListMask
        leadingMask={showLeftMask}
        trailingMask={showRightMask}
        enabled={Platform.OS === 'web' ? false : noMask !== true}
        location={maskLocation}
        css={`
          flex: 1;
        `}
      >
        <FlatList
          {...props}
          ref={listRef}
          horizontal={true}
          showsHorizontalScrollIndicator={false}
          onLayout={onListLayout}
          onContentSizeChange={onListContentSizeChange}
          onScroll={onListScroll}
        />
      </ListMask>
      {showScrollButtons && (
        <>
          <HorizontalScrollButton
            type="left"
            size={buttonSize}
            onPress={onScrollButtonPress}
            css={`
              position: absolute;
              left: ${-buttonSize / 2}px;
              top: 50%;
            `}
            style={{
              transform: [
                {
                  translateY: -buttonSize / 2,
                },
              ],
            }}
          />
          <HorizontalScrollButton
            type="right"
            size={buttonSize}
            onPress={onScrollButtonPress}
            css={`
              position: absolute;
              right: ${-buttonSize / 2}px;
              top: 50%;
            `}
            style={{
              transform: [
                {
                  translateY: -buttonSize / 2,
                },
              ],
            }}
          />
        </>
      )}
    </View>
  );
};

export const DecoratedHorizontalFlatList = memo(
  DecoratedHorizontalFlatListImpl,
) as typeof DecoratedHorizontalFlatListImpl;
